import React from 'react';

/**
 * Custom illustrated thumbnail for NOTEorious (AI document analyzer) — built
 * from shapes and icons instead of a screenshot. Pure-white canvas with the
 * site's dark-mode base colour (#121110) as the only ink, matching the Optimus
 * monochrome look. It depicts the product's story: a document is scanned, then
 * analysed into AI, voice and OCR outputs. Subtle SMIL animations run because
 * the card lives inside `.op-page` (exempt from the legacy motion kill-switch).
 */
const INK = '#121110';

const NoteoriousThumb: React.FC = () => (
  <svg
    viewBox="0 0 400 300"
    className="op-thumb w-full h-full bg-white transition-transform duration-700 ease-out group-hover:scale-105"
    preserveAspectRatio="xMidYMid meet"
    role="img"
    aria-label="NOTEorious — AI document analyzer"
  >
    {/* Canvas */}
    <rect width="400" height="300" fill="#ffffff" />

    {/* Faint dotted paper texture */}
    <g fill={INK} opacity="0.05">
      {Array.from({ length: 6 }).map((_, r) =>
        Array.from({ length: 9 }).map((_, c) => (
          <circle key={`${r}-${c}`} cx={22 + c * 45} cy={26 + r * 48} r="1.4" />
        ))
      )}
    </g>

    <defs>
      <clipPath id="noteSheetClip">
        <rect x="52" y="44" width="150" height="204" rx="12" />
      </clipPath>
    </defs>

    {/* Back sheet (depth) */}
    <rect x="62" y="52" width="150" height="204" rx="12" fill="#ffffff" stroke={INK} strokeOpacity="0.12" strokeWidth="1.5" />

    {/* Front sheet */}
    <rect x="52" y="44" width="150" height="204" rx="12" fill="#ffffff" stroke={INK} strokeWidth="1.8" />

    {/* Sheet contents */}
    <g>
      {/* Title */}
      <rect x="70" y="66" width="82" height="11" rx="4" fill={INK} />
      {/* Intro lines */}
      <rect x="70" y="90" width="114" height="6.5" rx="3.25" fill={INK} opacity="0.13" />
      <rect x="70" y="104" width="98" height="6.5" rx="3.25" fill={INK} opacity="0.13" />

      {/* OCR-detected handwriting region */}
      <rect x="66" y="120" width="122" height="46" rx="7" fill={INK} opacity="0.03" />
      <rect x="66" y="120" width="122" height="46" rx="7" fill="none" stroke={INK} strokeWidth="1.4" strokeDasharray="5 4" />
      {/* Detection corner brackets */}
      <path
        d="M74 120 H66 V128 M180 120 H188 V128 M74 166 H66 V158 M180 166 H188 V158"
        fill="none"
        stroke={INK}
        strokeWidth="2"
        strokeLinecap="round"
      />
      {/* Handwriting squiggle */}
      <path
        d="M74 148 q7 -13 13 0 t13 0 M104 150 q6 -15 12 -3 t2 8 M128 140 l7 16 M126 148 h13"
        fill="none"
        stroke={INK}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity="0.6"
      />

      {/* Body lines */}
      <rect x="70" y="182" width="108" height="6.5" rx="3.25" fill={INK} opacity="0.13" />
      <rect x="70" y="196" width="120" height="6.5" rx="3.25" fill={INK} opacity="0.13" />
      <rect x="70" y="210" width="84" height="6.5" rx="3.25" fill={INK} opacity="0.13" />
      <rect x="70" y="224" width="102" height="6.5" rx="3.25" fill={INK} opacity="0.13" />
    </g>

    {/* Scan beam (clipped to the sheet) */}
    <g clipPath="url(#noteSheetClip)">
      <rect x="52" width="150" height="20" fill={INK} opacity="0.06">
        <animate attributeName="y" values="44;228;44" dur="3.8s" repeatCount="indefinite" />
      </rect>
      <rect x="52" width="150" height="2.5" fill={INK} opacity="0.5">
        <animate attributeName="y" values="52;236;52" dur="3.8s" repeatCount="indefinite" />
      </rect>
    </g>

    {/* Dotted "analysis" fan from the document to the badges */}
    <g stroke={INK} strokeWidth="1.4" strokeDasharray="2 5" strokeLinecap="round" opacity="0.3" fill="none">
      <path d="M206 136 C242 116 252 94 283 84" />
      <path d="M208 150 H283" />
      <path d="M206 164 C242 184 252 206 283 216" />
    </g>

    {/* AI sparkle badge */}
    <g>
      <circle cx="310" cy="84" r="27" fill="#ffffff" stroke={INK} strokeWidth="1.8" />
      <path
        d="M310 66 C312 78 313 79 328 84 C313 89 312 90 310 102 C308 90 307 89 292 84 C307 79 308 78 310 66 Z"
        fill={INK}
      >
        <animate attributeName="opacity" values="1;0.4;1" dur="2.4s" repeatCount="indefinite" />
      </path>
      <path d="M323 71 l1.4 3.1 l3.1 1.4 l-3.1 1.4 l-1.4 3.1 l-1.4 -3.1 l-3.1 -1.4 l3.1 -1.4 Z" fill={INK} opacity="0.55" />
    </g>

    {/* Voice-input waveform badge */}
    <g>
      <circle cx="310" cy="150" r="27" fill="#ffffff" stroke={INK} strokeWidth="1.8" />
      {[
        { x: 297, min: 8, max: 18, dur: '1.0s', begin: '0s' },
        { x: 303, min: 12, max: 28, dur: '1.2s', begin: '0.15s' },
        { x: 309, min: 16, max: 34, dur: '0.9s', begin: '0.05s' },
        { x: 315, min: 12, max: 26, dur: '1.15s', begin: '0.2s' },
        { x: 321, min: 8, max: 20, dur: '1.05s', begin: '0.1s' },
      ].map((b) => (
        <rect key={b.x} x={b.x} width="3" rx="1.5" fill={INK}>
          <animate
            attributeName="height"
            values={`${b.min};${b.max};${b.min}`}
            dur={b.dur}
            begin={b.begin}
            repeatCount="indefinite"
          />
          <animate
            attributeName="y"
            values={`${150 - b.min / 2};${150 - b.max / 2};${150 - b.min / 2}`}
            dur={b.dur}
            begin={b.begin}
            repeatCount="indefinite"
          />
        </rect>
      ))}
    </g>

    {/* OCR text badge */}
    <g>
      <circle cx="310" cy="216" r="27" fill="#ffffff" stroke={INK} strokeWidth="1.8" />
      <text
        x="310"
        y="224"
        textAnchor="middle"
        fontFamily="'JetBrains Mono', monospace"
        fontSize="20"
        fontWeight="700"
        fill={INK}
      >
        Aa
      </text>
    </g>
  </svg>
);

export default NoteoriousThumb;
